import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { getPagePath, trans } from '@spotahome/soyuz/client';
import { Chip } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';

import './HeroAgenticInput.scss';

const MIN_HEIGHT_PX = 152;
const MAX_HEIGHT_PX = 320;

const PLACEHOLDER_COUNT = 10;
const PLACEHOLDER_KEYS = Array.from(
  { length: PLACEHOLDER_COUNT },
  (_, i) => `marketplace.agentic.home.input.placeholder.${i}`
);

// Typewriter animation timings. Type/erase budgets are per-phrase totals
// (so longer phrases simply type faster per character); the floor keeps
// very short phrases from strobing.
const TYPE_DURATION_MS = 1500;
const HOLD_DURATION_MS = 5000;
const ERASE_DURATION_MS = 700;
const MIN_CHAR_DELAY_MS = 20;

const charDelay = (totalMs, length) =>
  Math.max(MIN_CHAR_DELAY_MS, totalMs / Math.max(length, 1));

// Cycles through the provided phrases with a type → hold → erase → next
// loop. Pauses when `isPaused` flips true (user has draft text, so the
// placeholder is hidden anyway) and restarts from phrase 0 on resume —
// simpler than tracking resume state, and visually identical since the
// animation is invisible while paused. Respects prefers-reduced-motion
// by skipping the animation and freezing on the first phrase.
const usePlaceholderRotation = (phrases, isPaused) => {
  const [text, setText] = useState('');

  useEffect(() => {
    if (isPaused || phrases.length === 0) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setText(phrases[0]);
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    const schedule = (fn, ms) => {
      timeoutId = setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
    };

    const runPhrase = idx => {
      const phrase = phrases[idx % phrases.length];
      const typeStep = charDelay(TYPE_DURATION_MS, phrase.length);
      const eraseStep = charDelay(ERASE_DURATION_MS, phrase.length);

      const eraseChar = i => {
        setText(phrase.slice(0, i));
        if (i > 0) {
          schedule(() => eraseChar(i - 1), eraseStep);
        } else {
          schedule(() => runPhrase(idx + 1), typeStep);
        }
      };

      const typeChar = i => {
        setText(phrase.slice(0, i));
        if (i < phrase.length) {
          schedule(() => typeChar(i + 1), typeStep);
        } else {
          schedule(() => eraseChar(phrase.length - 1), HOLD_DURATION_MS);
        }
      };

      typeChar(1);
    };

    runPhrase(0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isPaused, phrases]);

  return text;
};

// Hero entry-point for the agentic experience. Captures the user's first
// query on the homepage and hands it off to /agentic-search via a `?q=`
// URL param — the search-agentic page consumes it once on bootstrap and
// strips it from the URL. We keep this dumb on purpose: no providers, no
// chat session here, just a styled textarea that auto-grows up to
// MAX_HEIGHT_PX and navigates on submit.
export const HeroAgenticInput = ({ className = '', cities = [] }) => {
  const [draft, setDraft] = useState('');
  const textareaRef = useRef(null);

  const navigate = useCallback(text => {
    if (typeof window === 'undefined') return;
    const basePath = getPagePath('SearchAgentic');
    window.location.href = `${basePath}?q=${encodeURIComponent(text)}`;
  }, []);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      navigate(text);
    },
    [draft, navigate]
  );

  // Enter submits, Shift+Enter inserts a newline. Mirrors the convention
  // most chat surfaces use, including the in-conversation input the user
  // sees on /agentic-search.
  const handleKeyDown = useCallback(
    e => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      navigate(text);
    },
    [draft, navigate]
  );

  // Each city chip seeds the agent with a `{{city}}`-interpolated message
  // from a Loco key, so PMs can tune the wording without code changes.
  const handleCityClick = useCallback(
    cityName => {
      const seed = trans(
        'marketplace.agentic.home.popular_cities.send_template',
        { city: cityName }
      );
      navigate(seed);
    },
    [navigate]
  );

  // Resize the textarea to fit its content, capped at MAX_HEIGHT_PX (beyond
  // which it falls back to internal scrolling). Reset to `auto` first so
  // the textarea can shrink when content is deleted.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const fit = Math.max(
      MIN_HEIGHT_PX,
      Math.min(el.scrollHeight, MAX_HEIGHT_PX)
    );
    el.style.height = `${fit}px`;
  }, [draft]);

  const isSendDisabled = !draft.trim();
  const hasCities = Array.isArray(cities) && cities.length > 0;

  // Locale-resolved phrases for the typewriter. Memoised so the hook's
  // effect dep stays referentially stable across renders.
  const placeholderPhrases = useMemo(
    () => PLACEHOLDER_KEYS.map(key => trans(key)),
    []
  );
  const animatedPlaceholder = usePlaceholderRotation(
    placeholderPhrases,
    draft.length > 0
  );

  return (
    <form
      className={`hero-agentic-input ${className || ''}`}
      onSubmit={handleSubmit}
    >
      <div className="hero-agentic-input__card">
        <div className="hero-agentic-input__editor">
          <DSIcon
            name="auto_awesome"
            size="sm"
            className="hero-agentic-input__leading-icon"
          />
          <textarea
            ref={textareaRef}
            rows={3}
            className="hero-agentic-input__input"
            placeholder={animatedPlaceholder}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={trans('marketplace.agentic.home.input.aria')}
          />
          <button
            type="submit"
            className="hero-agentic-input__send-button"
            aria-label={trans('marketplace.agentic.home.input.sendLabel')}
            disabled={isSendDisabled}
          >
            <DSIcon name="arrow_right" size="sm" />
          </button>
        </div>
        {hasCities && (
          <div className="hero-agentic-input__suggestions">
            <span className="hero-agentic-input__suggestions-label">
              {trans('marketplace.agentic.home.popular_cities.label')}
            </span>
            <div className="hero-agentic-input__suggestions-chips">
              {cities.map(city => (
                <Chip.Filter
                  key={city.id}
                  label={city.cityName}
                  className="hero-agentic-input__suggestion-chip"
                  onClick={() => handleCityClick(city.cityName)}
                  renderLeadingIcon={() => (
                    <DSIcon name="location_on-1" size="s" />
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

HeroAgenticInput.propTypes = {
  className: PropTypes.string,
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      cityName: PropTypes.string.isRequired
    })
  )
};

export default HeroAgenticInput;
